'use client';

import { useEffect, useState } from 'react';
import { getBasePath } from '../utils/basePath';
import { fetchJsonOnce, fetchJsonWithBackoff } from './fetchJson';
import type { BandcampItem } from './MediaItemTypes';
import { sortByNewest } from './sortByNewest';

export const fallbackBandcampReleases: BandcampItem[] = [
  {
    url: 'https://joev.bandcamp.com/track/tough-love-sailor-i-tribute',
    title: 'Tough Love (Sailor & I Tribute)',
    id: 'tough-love-sailor-i-tribute',
    type: 'track',
    date: '2020-03-01T00:00:00.000Z',
    source: 'bandcamp',
    checked: false,
    show: true,
    curated: true,
  },
  {
    url: 'https://joev.bandcamp.com/album/clsr-ep',
    title: 'CLSR EP',
    id: 'clsr-ep',
    type: 'album',
    date: '2020-06-01T00:00:00.000Z',
    source: 'bandcamp',
    checked: false,
    show: true,
    curated: true,
  },
];

/**
 * Normalizes a raw input object into a typed BandcampItem.
 */
function normalizeBandcampItem(input: Partial<BandcampItem>): BandcampItem | null {
  const url = input.url || '';
  if (!url) return null;

  const idFromUrl = (() => {
    try {
      const parts = new URL(url).pathname.split('/').filter(Boolean);
      return parts[1] || parts[0];
    } catch {
      return undefined;
    }
  })();

  const id = input.id || idFromUrl;
  if (!id) return null;

  const typeFromUrl = url.includes('/album/') ? 'album' : 'track';

  return {
    id,
    url,
    title: input.title || id.replace(/[-_]+/g, ' '),
    type: (input.type as 'album' | 'track') || typeFromUrl,
    date: input.date || null,
    source: 'bandcamp',
    checked: input.checked ?? false,
    show: input.show ?? true,
    curated: input.curated ?? false,
  };
}

/**
 * React hook for fetching and managing Bandcamp releases.
 */
export function useBandcampItems() {
  const [items, setItems] = useState<BandcampItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryToken, setRetryToken] = useState(0);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const fetcher = retryToken > 0 ? fetchJsonWithBackoff : fetchJsonOnce;
      const basePath = getBasePath();
      const result = await fetcher<{ releases?: Partial<BandcampItem>[] }>(
        `${basePath}/data/bandcamp-releases.json`
      );

      if (!result.ok) {
        setItems(sortByNewest(fallbackBandcampReleases));
        setError(result.reason === 'exception' ? result.error : null);
        setLoading(false);
        return;
      }

      const releases = Array.isArray(result.data.releases) ? result.data.releases : [];
      const normalized = releases
        .map((release) => normalizeBandcampItem(release))
        .filter((release): release is BandcampItem => Boolean(release));

      setItems(sortByNewest(normalized.length ? normalized : fallbackBandcampReleases));
      setError(null);
      setLoading(false);
    };

    load();
  }, [retryToken]);

  const retry = () => setRetryToken((token) => token + 1);

  return {
    items,
    loading,
    error,
    retry,
  };
}
